import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { WaziWordmark } from "../components/Logo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Phone, KeyRound, AlertCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";

const WorkerLogin = () => {
  const nav = useNavigate();
  const [step, setStep] = useState("phone"); // 'phone' | 'pin'
  const [phone, setPhone] = useState("");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [hint, setHint] = useState(null);

  const requestPin = async (e) => {
    e?.preventDefault();
    if (!phone) return;
    setBusy(true);
    try {
      const res = await api.post("/worker/login/request", { whatsapp_number: phone });
      setStep("pin");
      if (res.data.sent_via === "whatsapp") {
        toast.success("PIN sent on WhatsApp");
        setHint("Check your WhatsApp for a 6-digit code from Wazi.");
      } else {
        toast.message("PIN generated (sandbox mode — ask admin if you're a tester)");
        setHint("WhatsApp delivery is gated (account in Meta review). For now, ask the Wazi admin to read the PIN from logs.");
      }
    } catch {
      toast.error("Couldn't send PIN. Check the number and try again.");
    } finally { setBusy(false); }
  };

  const verifyPin = async (e) => {
    e?.preventDefault();
    if (!pin) return;
    setBusy(true);
    try {
      const res = await api.post("/worker/login/verify", { whatsapp_number: phone, pin });
      localStorage.setItem("wazi_worker_token", res.data.access_token);
      localStorage.setItem("wazi_worker", JSON.stringify(res.data.worker));
      toast.success(`Welcome back, ${res.data.worker.full_name.split(" ")[0]}!`);
      nav("/worker");
    } catch (e) {
      const m = e?.response?.data?.detail || "Invalid code";
      toast.error(m);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#F7FAFC] flex flex-col items-center justify-center p-4">
      <Link to="/" className="mb-8"><WaziWordmark size={36} /></Link>
      <div className="w-full max-w-md bg-white border border-[#E2E8F0] rounded-2xl p-8 shadow-sm" data-testid="worker-login-card">
        <h1 className="font-heading text-2xl font-bold text-[#2D3748]">Painter sign in</h1>
        <p className="mt-1 text-sm text-[#718096]">Use your WhatsApp number. We'll send a 6-digit code.</p>

        {step === "phone" ? (
          <form onSubmit={requestPin} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="wp" className="text-[#2D3748] font-heading font-medium">WhatsApp number</Label>
              <div className="relative mt-1.5">
                <Phone className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#718096]" />
                <Input
                  id="wp"
                  data-testid="worker-phone-input"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+27 82 555 1234"
                  className="pl-9 h-11 rounded-xl border-[#E2E8F0]"
                  required
                />
              </div>
            </div>
            <Button type="submit" disabled={busy} data-testid="worker-request-pin-btn" className="w-full h-11 rounded-xl bg-[#25D366] hover:bg-[#16A34A] text-white font-medium wazi-cta-glow">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send my code"}
            </Button>
          </form>
        ) : (
          <form onSubmit={verifyPin} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="pn" className="text-[#2D3748] font-heading font-medium">6-digit code</Label>
              <div className="relative mt-1.5">
                <KeyRound className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#718096]" />
                <Input
                  id="pn"
                  data-testid="worker-pin-input"
                  inputMode="numeric"
                  maxLength={6}
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
                  placeholder="123456"
                  className="pl-9 h-11 rounded-xl border-[#E2E8F0] font-mono tracking-widest"
                  required
                />
              </div>
            </div>
            {hint && (
              <div className="flex items-start gap-2 text-xs text-[#718096] bg-[#F7FAFC] border border-[#E2E8F0] rounded-lg p-2.5">
                <AlertCircle className="w-3.5 h-3.5 text-[#FFB400] mt-0.5 flex-shrink-0" /> {hint}
              </div>
            )}
            <Button type="submit" disabled={busy || pin.length < 6} data-testid="worker-verify-pin-btn" className="w-full h-11 rounded-xl bg-[#25D366] hover:bg-[#16A34A] text-white font-medium wazi-cta-glow">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
            </Button>
            <button type="button" onClick={() => { setStep("phone"); setPin(""); }} className="w-full text-xs text-[#718096] hover:text-[#2D3748]" data-testid="worker-change-number">
              ← Use a different number
            </button>
          </form>
        )}
      </div>
      <Link to="/" className="mt-6 text-sm text-[#718096] hover:text-[#2D3748]">← Back to home</Link>
    </div>
  );
};

export default WorkerLogin;
